import React, { createContext, useContext, useState, Dispatch, SetStateAction } from "react";
import { AdminPersonnel } from "./AdminPersonnelContext";
import { useAlert } from "./AlertsContext";
import { useGlobalModal } from "./GlobalModalContext";
import { AcceptPersonnelDtoType } from "../api/PersonnelApi";
import {
    bulkAccept,
    bulkAcceptAndSendQuestionnaire,
    ExemptPersonnelDTO,
    bulkExempt,
    bulkRolloverInProgress,
    toBulkResolveResultsFromRosterAction,
    BulkRosterActionResponse,
} from "../api/RosterApi";
import { getApiErrorMessage } from "../util/ApiErrorUtil";
import { PersonnelTypes } from "../constants/PersonnelTypes";

export type BulkActionSearchContext = {
    personnelAdvancedSearch: (...args: any[]) => void;
    adminSelectedMenuOptions: Array<any>;
    adminAffiliationFilters: Array<any>;
    adminSearchText: Array<any> | string;
};

type SelectedPersonnelContext = {
    initialSelectedPersonnel: AdminPersonnel[];
    setInitialSelectedPersonnel: Dispatch<SetStateAction<AdminPersonnel[]>>;
    selectedPersonnel: AdminPersonnel[];
    setSelectedPersonnel: Dispatch<SetStateAction<AdminPersonnel[]>>;
    removePersonnel(idToRemove: number): void;
    drugTestOnChange(idToChange: number): void;
    bulkAcceptPersonnel(searchContext: BulkActionSearchContext): Promise<void>;
    bulkAcceptAndSendQuestionnairePersonnel(searchContext: BulkActionSearchContext): Promise<void>;
    bulkExemptPersonnel(searchContext: BulkActionSearchContext): Promise<void>;
    bulkRolloverInProgressPersonnel(searchContext: BulkActionSearchContext): Promise<void>;
    bulkActionInFlight: boolean;
    exemptComment: string;
    setExemptComment: Dispatch<SetStateAction<string>>;
};

const SelectedPersonnelContext = createContext<SelectedPersonnelContext>({
    initialSelectedPersonnel: null,
    setInitialSelectedPersonnel: null,
    selectedPersonnel: null,
    setSelectedPersonnel: null,
    removePersonnel: null,
    drugTestOnChange: null,
    bulkAcceptPersonnel: null,
    bulkAcceptAndSendQuestionnairePersonnel: null,
    bulkExemptPersonnel: null,
    exemptComment: null,
    setExemptComment: null,
    bulkRolloverInProgressPersonnel: null,
    bulkActionInFlight: false,
});

const SelectedPersonnelContextProvider: React.FC = ({ children }) => {
    const [initialSelectedPersonnel, setInitialSelectedPersonnel] = useState([]);
    const [selectedPersonnel, setSelectedPersonnel] = useState([]);
    const [exemptComment, setExemptComment] = useState<string>("");
    const [bulkActionInFlight, setBulkActionInFlight] = useState(false);
    const showAlert = useAlert();
    const { dispatch, closeModal } = useGlobalModal();

    const removePersonnel = (idToRemove: number) => {
        const indexToRemove = selectedPersonnel.findIndex(personnel => personnel.id === idToRemove);
        if (indexToRemove > -1) {
            selectedPersonnel.splice(indexToRemove, 1);
            const newData = [...selectedPersonnel];
            setSelectedPersonnel(newData);
        }
    };

    const drugTestOnChange = (idToChange: number) => {
        selectedPersonnel?.forEach(personnel => {
            if (personnel.id === idToChange) {
                personnel.drugTest = !personnel.drugTest;
            }
        });
        setSelectedPersonnel([...selectedPersonnel]);
    };

    const refreshSearch = (searchContext: BulkActionSearchContext) => {
        setTimeout(
            () =>
                searchContext.personnelAdvancedSearch(
                    searchContext.adminSearchText && searchContext.adminSearchText.length !== 0
                        ? searchContext.adminSearchText
                        : null,
                    searchContext.adminAffiliationFilters
                        ? searchContext.adminAffiliationFilters.map(opt => opt.orgId)
                        : null,
                    searchContext.adminSelectedMenuOptions.map(opt => opt.id),
                    PersonnelTypes.CLUB,
                ),
            1000,
        );
    };

    const openBulkResults = (
        response: BulkRosterActionResponse,
        searchContext: BulkActionSearchContext,
        emptyFallback: string,
    ) => {
        const results = toBulkResolveResultsFromRosterAction(response);
        const total =
            (results.moved?.length ?? 0) +
            (results.failed?.length ?? 0) +
            (results.restored?.length ?? 0) +
            (results.sentToSos?.length ?? 0);
        if (total === 0) {
            showAlert(emptyFallback, "danger");
            closeModal();
            refreshSearch(searchContext);
            return;
        }
        if ((results.failed?.length ?? 0) === 0) {
            showAlert(`${results.moved.length} succeeded`, "success");
        } else if ((results.moved?.length ?? 0) === 0) {
            showAlert(`${results.failed.length} failed`, "danger");
        } else {
            showAlert(
                `${results.moved.length} succeeded, ${results.failed.length} failed`,
                "danger",
            );
        }
        closeModal();
        dispatch({
            type: "openBulkReviewResolveResultsModal",
            bulkReviewResolveResults: results,
            personnelAdvancedSearch: searchContext.personnelAdvancedSearch,
            adminSelectedMenuOptions: searchContext.adminSelectedMenuOptions,
            adminAffiliationFilters: searchContext.adminAffiliationFilters,
            adminSearchText: searchContext.adminSearchText,
        });
        refreshSearch(searchContext);
    };

    const runBulk = async (
        request: () => Promise<BulkRosterActionResponse>,
        searchContext: BulkActionSearchContext,
        failureFallback: string,
    ) => {
        if (bulkActionInFlight) {
            return;
        }
        setBulkActionInFlight(true);
        try {
            const response = await request();
            openBulkResults(response, searchContext, failureFallback);
        } catch (error) {
            showAlert(getApiErrorMessage(error, failureFallback), "danger");
            refreshSearch(searchContext);
        } finally {
            setBulkActionInFlight(false);
        }
    };

    const bulkAcceptPersonnel = async (searchContext: BulkActionSearchContext) => {
        if (selectedPersonnel?.length > 0) {
            const bulkAcceptPersonnelDTO = { acceptPersonnelList: Array<AcceptPersonnelDtoType>() };
            selectedPersonnel.forEach(personnel => {
                const acceptPersonnelDTO: AcceptPersonnelDtoType = {
                    personnelId: personnel.id,
                    bgCheckCriminal: true,
                    bgCheckCredit: true,
                    bgCheckEducation: true,
                    drugTesting: false,
                };
                bulkAcceptPersonnelDTO.acceptPersonnelList.push(acceptPersonnelDTO);
            });
            if (bulkAcceptPersonnelDTO.acceptPersonnelList.length > 0) {
                await runBulk(
                    () => bulkAccept(bulkAcceptPersonnelDTO),
                    searchContext,
                    "Error bulk accepting personnel, Please contact BIGS Systems.",
                );
            }
        }
    };

    const bulkAcceptAndSendQuestionnairePersonnel = async (
        searchContext: BulkActionSearchContext,
    ) => {
        if (selectedPersonnel?.length > 0) {
            const bulkDto = {
                acceptAndSendQuestionnaireList: selectedPersonnel.map(personnel => ({
                    personnelId: personnel.id,
                })),
            };
            await runBulk(
                () => bulkAcceptAndSendQuestionnaire(bulkDto),
                searchContext,
                "Error accepting personnel and sending questionnaire. Please contact BIGS Systems.",
            );
        }
    };

    const bulkExemptPersonnel = async (searchContext: BulkActionSearchContext) => {
        if (selectedPersonnel?.length > 0) {
            const bulkExemptPersonnelDTO = { exemptPersonnelList: Array<ExemptPersonnelDTO>() };
            selectedPersonnel.forEach(personnel => {
                const exemptPersonnelDTO: ExemptPersonnelDTO = {
                    personnelId: personnel.id,
                    comment: exemptComment,
                };
                bulkExemptPersonnelDTO.exemptPersonnelList.push(exemptPersonnelDTO);
            });
            if (bulkExemptPersonnelDTO.exemptPersonnelList.length > 0) {
                await runBulk(
                    () => bulkExempt(bulkExemptPersonnelDTO),
                    searchContext,
                    "Error bulk exempting personnel, Please contact BIGS Systems.",
                );
            }
        }
    };

    const bulkRolloverInProgressPersonnel = async (searchContext: BulkActionSearchContext) => {
        if (selectedPersonnel?.length > 0) {
            await runBulk(
                () =>
                    bulkRolloverInProgress({
                        personnelIds: selectedPersonnel.map(sp => sp.id),
                    }),
                searchContext,
                "Error bulk rollover / move to in progress. Please contact BIGS Systems.",
            );
        }
    };

    return (
        <SelectedPersonnelContext.Provider
            value={{
                initialSelectedPersonnel,
                setInitialSelectedPersonnel,
                selectedPersonnel,
                setSelectedPersonnel,
                removePersonnel,
                drugTestOnChange,
                bulkAcceptPersonnel,
                bulkAcceptAndSendQuestionnairePersonnel,
                bulkExemptPersonnel,
                exemptComment,
                setExemptComment,
                bulkRolloverInProgressPersonnel,
                bulkActionInFlight,
            }}
        >
            {children}
        </SelectedPersonnelContext.Provider>
    );
};

const useSelectedPersonnel = (): SelectedPersonnelContext => {
    const context = useContext<SelectedPersonnelContext>(SelectedPersonnelContext);
    if (context === undefined) {
        throw new Error(`useAddPersonnel must be used within a SelectedPersonnelContextProvider`);
    }
    return context;
};

export { SelectedPersonnelContextProvider, useSelectedPersonnel };
